import React from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import { formatMXN } from "../lib/api";
import {
  ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Wallet, User,
  Calendar, Clock, Tag, FileText, Repeat, Info, Lock,
} from "lucide-react";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function fullDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate()} de ${MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
}
function timeOf(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

export default function TransactionDetailSheet({ tx, onClose, onEdit, locked = false }) {
  if (!tx) return null;
  const isIncome = tx.type === "ingreso";
  const isTransfer = tx.type === "transferencia";
  const Icon = isTransfer ? ArrowLeftRight : isIncome ? ArrowDownLeft : ArrowUpRight;
  const tone = isTransfer
    ? "bg-sage-50 text-sage-700"
    : isIncome
    ? "bg-sage-100 text-sage-900"
    : "bg-clay/10 text-clay";
  const sign = isTransfer ? "" : isIncome ? "+ " : "− ";
  const origen = tx.from_recurrence_id
    ? "Pago de recurrencia"
    : isTransfer
    ? "Transferencia entre cuentas"
    : "Registro manual";

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent
        side="bottom"
        data-testid="tx-detail-sheet"
        className="bg-bone border-line rounded-t-3xl p-0 max-w-md mx-auto overflow-y-auto overscroll-contain"
        style={{ maxHeight: "92dvh", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 1.5rem)" }}
      >
        <div className="mx-auto mt-3 mb-1 h-1.5 w-12 rounded-full bg-line/80" aria-hidden />
        <SheetHeader className="text-left px-6 pt-1">
          <SheetTitle className="sr-only">Detalle de movimiento</SheetTitle>
        </SheetHeader>

        {/* Ficha principal */}
        <div className="px-6 pt-2 pb-3 text-center">
          <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl ${tone}`}>
            <Icon size={22} strokeWidth={2.2} />
          </div>
          <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.16em] text-soft">
            {isTransfer ? "Transferencia" : isIncome ? "Ingreso" : "Gasto"}
          </p>
          <p
            className={`mt-1 font-display text-4xl font-extrabold tabular ${
              isIncome ? "text-sage-900" : isTransfer ? "text-ink" : "text-clay"
            }`}
            data-testid="tx-detail-amount"
          >
            {sign}{formatMXN(tx.amount)}
          </p>
          <p className="mt-1 text-sm font-semibold text-ink" data-testid="tx-detail-category">
            {tx.category || (isTransfer ? "Movimiento entre cuentas" : isIncome ? "Ingreso" : "Gasto")}
          </p>
        </div>

        {/* Fila de datos */}
        <div className="px-6 pb-3 space-y-2">
          <DataRow icon={Wallet} label="Cuenta" value={tx.account_name || "—"} testid="tx-detail-account" />
          <DataRow icon={Calendar} label="Fecha" value={fullDate(tx.date)} testid="tx-detail-date" />
          <DataRow icon={Clock} label="Registrado" value={
            `${fullDate(tx.created_at)}${timeOf(tx.created_at) ? " · " + timeOf(tx.created_at) : ""}`
          } testid="tx-detail-created-at" />
          {tx.registered_by_name && (
            <DataRow icon={User} label="Capturado por" value={tx.registered_by_name} testid="tx-detail-by" />
          )}
          <DataRow icon={Info} label="Origen" value={origen} testid="tx-detail-origin" />
          {tx.category && (
            <DataRow icon={Tag} label="Categoría" value={tx.category} testid="tx-detail-cat" />
          )}
          {tx.note && (
            <DataRow icon={FileText} label="Nota" value={tx.note} testid="tx-detail-note" />
          )}
          {tx.from_recurrence_id && (
            <DataRow icon={Repeat} label="Recurrencia" value={
              tx.from_recurrence_period ? `Periodo ${tx.from_recurrence_period}` : "Referencia interna"
            } testid="tx-detail-recurrence" />
          )}
          {tx.scope === "shared" && (
            <DataRow icon={Info} label="Espacio" value="Compartido · dinero común" testid="tx-detail-scope" />
          )}
        </div>

        {onEdit && !locked && (
          <div className="px-6 pb-2">
            <button
              type="button"
              onClick={onEdit}
              data-testid="tx-detail-edit"
              className="w-full bg-white border border-line text-ink font-bold py-3 rounded-2xl hover:bg-sage-50"
            >
              Editar o eliminar
            </button>
          </div>
        )}
        {locked && (
          <div className="px-6 pb-2">
            <div
              data-testid="tx-detail-locked"
              className="w-full inline-flex items-center justify-center gap-2 bg-bone border border-line text-soft py-3 rounded-2xl text-sm"
            >
              <Lock size={14} /> Este mes ya está cerrado
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DataRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-line/70 rounded-2xl px-4 py-3 flex items-center gap-3" data-testid={testid}>
      <div className="h-8 w-8 rounded-lg bg-bone text-soft flex items-center justify-center shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-soft">{label}</p>
        <p className="text-sm text-ink truncate mt-0.5">{value}</p>
      </div>
    </div>
  );
}
